/**
 * The app in Spanish, for whoever asks (ADR 0325).
 *
 * One language for the whole screen, chosen by a button in the top bar and
 * remembered on the phone. Only what staff read changes: a customer's page,
 * a crew's packet, every email, text and PDF that leaves the building stays
 * in English whatever the phone is set to. The product names on an estimate
 * stay in English too — that is what the supplier calls them.
 *
 * How it is built, briefly, for whoever wraps the next screen:
 *
 * - `t("Sign out")` looks the English up in the Spanish dictionary and
 *   returns the English itself when there is no entry. English is the key,
 *   so a screen reads the same in the source as it does on an English phone,
 *   and a string that comes from data (a stage name, a checklist step) is
 *   translated wherever its English happens to be in the dictionary.
 * - `t("{n} jobs", { n })` fills the braces after the lookup, so the Spanish
 *   can put the number where Spanish puts it.
 * - The dictionary is one file per screen under `./es/`, merged in
 *   `./es/index.ts`. `scripts/check-spanish.mjs` fails the build on a key
 *   that is wrapped in `t()` but missing from the dictionary, so a screen
 *   cannot quietly fall back to English.
 * - The language is read by `t` straight from this module rather than from
 *   React context, so that a helper, a confirm() or a toast can translate as
 *   easily as a component. Changing it redraws the screens (keyed on the
 *   language in App.tsx, below App's own one-time startup work), which is
 *   what makes every screen re-read it.
 */

import { es } from "./es/index.js";

export type Language = "en" | "es";

/** The two choices, as the button cycles them. */
export const LANGUAGES: readonly Language[] = ["en", "es"];

/** What the button says the app is in. */
export const LANGUAGE_LABEL: Record<Language, string> = {
  en: "English",
  es: "Español",
};

const KEY = "roof-board:language";

let current: Language = "en";
const listeners = new Set<(language: Language) => void>();

/** The language the app is drawn in. */
export function language(): Language {
  return current;
}

/**
 * Read the remembered choice, once, before the first paint.
 *
 * A customer's or a crew's page never translates, whatever this phone's
 * choice: those pages are read by somebody who did not choose. `public` is
 * App's own answer for the URL (`isPublicRoute` in publicRoutes.ts), so a
 * route App serves to an outsider is English without being listed here
 * again; the path prefixes below are a second net under it.
 */
export function settleLanguage(pathname: string, isPublic = false): Language {
  if (isPublic || isPublicPath(pathname)) {
    current = "en";
    return current;
  }
  current = storedLanguage();
  return current;
}

/** `/p/`, `/c/`, `/cert/`, `/s/`, `/storms/`, `/go/`, `/colors`, `/sub/`. */
export function isPublicPath(pathname: string): boolean {
  return /^\/(p|c|cert|s|storms|go|colors|sub)(\/|$)/.test(pathname);
}

export function storedLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(KEY);
    return stored === "es" ? "es" : "en";
  } catch {
    return "en";
  }
}

/** Choose a language, remember it on this phone, and tell the app. */
export function setLanguage(next: Language): void {
  current = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    /* The screen still changes; only the memory of it is lost. */
  }
  for (const listener of listeners) listener(next);
}

/** The other one: the button toggles rather than cycles, there are two. */
export function nextLanguage(now: Language): Language {
  return now === "en" ? "es" : "en";
}

/** Hear about a change. Returns the unsubscribe. */
export function onLanguageChange(
  listener: (language: Language) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `{name}` placeholders, filled from `vars`. */
export type Vars = Record<string, string | number>;

/**
 * The English, in the app's language.
 *
 * Unknown English comes back as itself, so a screen that nobody has
 * translated yet is an English screen rather than a broken one.
 */
export function t(english: string, vars?: Vars): string {
  const text = current === "es" ? (es[english] ?? english) : english;
  return vars ? fill(text, vars) : text;
}

/**
 * One or many: `tn(count, "1 job", "{n} jobs")`.
 *
 * Both forms are keys in their own right, so the Spanish can be a different
 * sentence rather than an English one with a letter stuck on.
 */
export function tn(n: number, one: string, many: string, vars?: Vars): string {
  return t(n === 1 ? one : many, { n, ...vars });
}

/** Fill `{name}` from `vars`; a brace with no value is left as it is. */
export function fill(text: string, vars: Vars): string {
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** For tests: reset to English and forget the phone's choice. */
export function resetLanguageForTests(): void {
  current = "en";
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* no storage, nothing to forget */
  }
}
